/**
 * Deal Brief service — AI-generated strategic briefs per match.
 */
import api from './api';
import { ApiResponse } from '../types';
import {
  DealBrief,
  DealBriefListFilters,
  DealBriefListResponse,
} from '../types/dealBrief';

export async function generateBrief(
  matchId: number,
): Promise<ApiResponse<DealBrief>> {
  try {
    const response = await api.post(`/matches/${matchId}/brief/generate`);
    return { data: response.data };
  } catch (error: any) {
    return {
      error:
        error.response?.data?.detail ||
        'Failed to generate deal brief (Anthropic API key may not be configured).',
    };
  }
}

export async function fetchLatestBrief(
  matchId: number,
): Promise<ApiResponse<DealBrief | null>> {
  try {
    const response = await api.get(`/matches/${matchId}/brief`);
    return { data: response.data };
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { data: null };
    }
    return {
      error: error.response?.data?.detail || 'Failed to load deal brief',
    };
  }
}

export async function fetchBriefHistory(
  matchId: number,
): Promise<ApiResponse<DealBrief[]>> {
  try {
    const response = await api.get(`/matches/${matchId}/brief/history`);
    return { data: Array.isArray(response.data) ? response.data : [] };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load brief history',
    };
  }
}

export async function listBriefs(
  filters?: DealBriefListFilters,
): Promise<ApiResponse<DealBriefListResponse>> {
  try {
    const response = await api.get('/deal-briefs', { params: filters });
    const payload = response.data;
    return {
      data: {
        briefs: payload?.briefs || [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        size: payload?.size ?? 20,
        pages: payload?.pages ?? 1,
      },
    };
  } catch (error: any) {
    return {
      error: error.response?.data?.detail || 'Failed to load deal briefs',
    };
  }
}

const dealBriefService = {
  generateBrief,
  fetchLatestBrief,
  fetchBriefHistory,
  listBriefs,
};

export default dealBriefService;
